import {
  getClienteNotes,
  setNotesPayload,
  TIPO_PF,
  type Cliente,
  type ClienteCreate,
  type ClienteUpdate,
} from '@/api/clientes';
import { validateCpf, validateCnpj } from '@/lib/formatters';

// --- Constantes ---

// Fonte única em constants/ufs.ts (reusada pelo Secretário e outros formulários).
export { UFS } from '@/constants/ufs';

export const ESTADOS_CIVIS = [
  { value: 'solteiro', label: 'Solteiro(a)' },
  { value: 'casado', label: 'Casado(a)' },
  { value: 'divorciado', label: 'Divorciado(a)' },
  { value: 'viuvo', label: 'Viúvo(a)' },
  { value: 'uniao_estavel', label: 'União Estável' },
  { value: 'separado', label: 'Separado(a)' },
];

// --- Dados do formulário ---

export interface ClienteFormData {
  tipo_pessoa: string;
  nome: string;
  cpf: string;
  rg: string;
  razao_social: string;
  cnpj: string;
  email: string;
  telefone: string;
  telefone2: string;
  endereco_logradouro: string;
  endereco_numero: string;
  endereco_complemento: string;
  endereco_bairro: string;
  endereco_cidade: string;
  endereco_uf: string;
  endereco_cep: string;
  profissao: string;
  estado_civil: string;
  data_nascimento: string;
  nacionalidade: string;
  status: 'PROSPECTO' | 'ATIVO' | 'INATIVO';
  notes: string;
  escritorio_id: string;
}

export const emptyForm: ClienteFormData = {
  tipo_pessoa: TIPO_PF,
  nome: '',
  cpf: '',
  rg: '',
  razao_social: '',
  cnpj: '',
  email: '',
  telefone: '',
  telefone2: '',
  endereco_logradouro: '',
  endereco_numero: '',
  endereco_complemento: '',
  endereco_bairro: '',
  endereco_cidade: '',
  endereco_uf: '',
  endereco_cep: '',
  profissao: '',
  estado_civil: '',
  data_nascimento: '',
  nacionalidade: 'Brasileira',
  status: 'PROSPECTO',
  notes: '',
  escritorio_id: '',
};

export function clienteToForm(c: Cliente): ClienteFormData {
  return {
    tipo_pessoa: c.tipo_pessoa,
    nome: c.nome,
    cpf: c.cpf ?? '',
    rg: c.rg ?? '',
    razao_social: c.razao_social ?? '',
    cnpj: c.cnpj ?? '',
    email: c.email ?? '',
    telefone: c.telefone ?? '',
    telefone2: c.telefone2 ?? '',
    endereco_logradouro: c.endereco_logradouro ?? '',
    endereco_numero: c.endereco_numero ?? '',
    endereco_complemento: c.endereco_complemento ?? '',
    endereco_bairro: c.endereco_bairro ?? '',
    endereco_cidade: c.endereco_cidade ?? '',
    endereco_uf: c.endereco_uf ?? '',
    endereco_cep: c.endereco_cep ?? '',
    profissao: c.profissao ?? '',
    estado_civil: c.estado_civil ?? '',
    data_nascimento: c.data_nascimento ?? '',
    nacionalidade: c.nacionalidade ?? 'Brasileira',
    status: c.status,
    notes: getClienteNotes(c),
    escritorio_id: c.escritorio_id ?? SO_EU,
  };
}

// --- Payload ---

/**
 * O "Só eu" do formulário: cliente pessoal, escolha explícita (PLANO_CLIENTE_DO_ESCRITORIO R2).
 * Vazio, no cadastro, é o padrão: o servidor põe o cliente no escritório principal.
 */
export const SO_EU = '__so_eu__';

export function buildPayload(form: ClienteFormData, isPF: boolean, isEditing: boolean): ClienteCreate | ClienteUpdate {
  const payload: Record<string, unknown> = {
    tipo_pessoa: form.tipo_pessoa,
    nome: form.nome.trim(),
    email: form.email.trim() || undefined,
    telefone: form.telefone.trim() || undefined,
    telefone2: form.telefone2.trim() || undefined,
    endereco_logradouro: form.endereco_logradouro.trim() || undefined,
    endereco_numero: form.endereco_numero.trim() || undefined,
    endereco_complemento: form.endereco_complemento.trim() || undefined,
    endereco_bairro: form.endereco_bairro.trim() || undefined,
    endereco_cidade: form.endereco_cidade.trim() || undefined,
    endereco_uf: form.endereco_uf || undefined,
    endereco_cep: form.endereco_cep.trim() || undefined,
    profissao: form.profissao.trim() || undefined,
    estado_civil: form.estado_civil || undefined,
    data_nascimento: form.data_nascimento || undefined,
    nacionalidade: form.nacionalidade.trim() || undefined,
    status: form.status,
  };
  setNotesPayload(payload, form.notes.trim() || undefined);

  if (isPF) {
    payload.cpf = form.cpf.trim() || undefined;
    payload.rg = form.rg.trim() || undefined;
  } else {
    payload.razao_social = form.razao_social.trim() || undefined;
    payload.cnpj = form.cnpj.trim() || undefined;
  }

  if (form.escritorio_id === SO_EU) {
    payload.escritorio_id = null;
  } else if (form.escritorio_id) {
    payload.escritorio_id = form.escritorio_id;
  } else if (isEditing) {
    payload.escritorio_id = null;
  }
  // Cadastro sem escolha: o campo não vai, e o servidor decide (o escritório principal).

  return payload as unknown as ClienteCreate;
}

// --- Validação por step ---

export const TOTAL_STEPS = 5;

export const STEP_LABELS = [
  'Identificação',
  'Contato',
  'Endereço',
  'Complementar',
  'Documentos',
];

export function validateStep(step: number, form: ClienteFormData, isPF: boolean): Record<string, string> {
  const errs: Record<string, string> = {};

  if (step === 0) {
    if (!form.nome.trim()) errs.nome = 'Nome é obrigatório';
    if (isPF) {
      if (!form.cpf.trim()) errs.cpf = 'CPF é obrigatório para pessoa física';
      else if (!validateCpf(form.cpf)) errs.cpf = 'CPF inválido';
    } else {
      if (!form.cnpj.trim()) errs.cnpj = 'CNPJ é obrigatório para pessoa jurídica';
      else if (!validateCnpj(form.cnpj)) errs.cnpj = 'CNPJ inválido';
    }
  }

  if (step === 1) {
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'E-mail inválido';
    }
  }

  // Steps 2 e 3: sem campos obrigatórios

  return errs;
}
